import type { getResultTypeCounts } from '../../../common/utils/quick-find/analytics';
import { State } from '../../../common/utils/quick-find/state';
import type { ResultsKind } from '../perceived-latency-context';
import type { QuickFindContent } from './types';

const getResultSources = (content: QuickFindContent | undefined) => ({
	state: content?.state,
	searchResults:
		content?.state === State.POSTQUERY_RESULTS
			? content.searchResults
			: content?.state === State.POSTQUERY_LOADING
				? content.staleSearchResults
				: undefined,
	peopleAndTeams:
		content?.state === State.POSTQUERY_RESULTS
			? content.peopleAndTeams
			: content?.state === State.POSTQUERY_LOADING
				? content.stalePeopleAndTeams
				: undefined,
	recentActivities: content && 'recentActivities' in content ? content.recentActivities : undefined,
	recommendedDocuments:
		content && 'recommendedDocuments' in content ? content.recommendedDocuments : undefined,
});

// Source identity distinguishes a newly applied response/refinement from retained rows.
// Rendered counts exclude data that the dialog has filtered out. Read them lazily so
// unchanged sources and skeleton-only updates do not require another DOM scan.
export const getResultsObservationKind = (
	content: QuickFindContent,
	previousContent: QuickFindContent | undefined,
	readResultTypeCounts: typeof getResultTypeCounts,
): ResultsKind | undefined => {
	if (!content) {
		return undefined;
	}
	if (content.state === State.POSTQUERY_NO_RESULTS) {
		return content.state !== previousContent?.state ? 'empty' : undefined;
	}
	if (content.state === State.POSTQUERY_ERROR) {
		return content.state !== previousContent?.state ? 'error' : undefined;
	}
	if (content.state !== State.POSTQUERY_RESULTS && content.state !== State.POSTQUERY_LOADING) {
		return undefined;
	}

	const sources = getResultSources(content);
	const previous = getResultSources(previousContent);
	const searchResultsChanged =
		(Boolean(sources.searchResults?.length) && sources.searchResults !== previous.searchResults) ||
		(Boolean(sources.peopleAndTeams?.length) && sources.peopleAndTeams !== previous.peopleAndTeams);
	if (searchResultsChanged) {
		const counts = readResultTypeCounts();
		if (counts.searchResults > 0 || counts.peopleAndTeams > 0) {
			return 'search';
		}
	}
	const recentResultsChanged =
		(Boolean(sources.recentActivities?.length) &&
			sources.recentActivities !== previous.recentActivities) ||
		(Boolean(sources.recommendedDocuments?.length) &&
			sources.recommendedDocuments !== previous.recommendedDocuments);
	if (content.state === State.POSTQUERY_LOADING && recentResultsChanged) {
		const counts = readResultTypeCounts();
		if (counts.recentActivities > 0 || counts.recommendations > 0) {
			return 'recent';
		}
	}
	return undefined;
};
